type SharedRequest<T> = {
  controller: AbortController
  consumers: number
  promise: Promise<T>
  settled: boolean
}

function abortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError')
}

async function waitForRequest<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) throw abortError()

  let rejectForAbort: (reason: DOMException) => void = () => {}
  const abortPromise = new Promise<never>((_, reject) => {
    rejectForAbort = reject
  })
  const onAbort = () => rejectForAbort(abortError())
  signal.addEventListener('abort', onAbort, {once: true})

  try {
    return await Promise.race([promise, abortPromise])
  } finally {
    signal.removeEventListener('abort', onAbort)
  }
}

export class SharedRequestManager<T> {
  private readonly requests = new Map<string, SharedRequest<T>>()

  async fetch(key: string, signal: AbortSignal, createRequest: (signal: AbortSignal) => Promise<T>): Promise<T> {
    if (signal.aborted) throw abortError()

    let request = this.requests.get(key)

    if (!request) {
      const controller = new AbortController()
      request = {controller, consumers: 0, promise: createRequest(controller.signal), settled: false}
      this.requests.set(key, request)
      void this.removeWhenSettled(key, request)
    }

    request.consumers += 1
    try {
      return await waitForRequest(request.promise, signal)
    } finally {
      request.consumers -= 1
      if (request.consumers === 0 && !request.settled) {
        if (this.requests.get(key) === request) this.requests.delete(key)
        request.controller.abort()
      }
    }
  }

  private async removeWhenSettled(key: string, request: SharedRequest<T>) {
    try {
      await request.promise
    } catch {
      // The fetch caller handles the request error.
    } finally {
      request.settled = true
      if (this.requests.get(key) === request) this.requests.delete(key)
    }
  }
}
